import { isMatch, merge } from 'lodash'
import { useRef } from 'react'
import type { NormalizedCacheObject } from '@apollo/client'
import { initializeApollo, setRequestLinkBusinessId } from 'lib/apollo-client'

interface UseInitializedApolloClientOptions {
  businessId: string | null
  initialCache: NormalizedCacheObject | undefined
  pathName: string
}

export const useInitializedApolloClient = ({
  businessId,
  initialCache,
  pathName,
}: UseInitializedApolloClientOptions) => {
  setRequestLinkBusinessId(businessId)
  const businessRef = useRef(businessId)
  const apolloClientRef = useRef<ReturnType<typeof initializeApollo> | null>(null)

  if (apolloClientRef.current === null) {
    apolloClientRef.current = initializeApollo()
  }
  const currentPathName = useRef<string | null>(null)

  const initializeCache = () => {
    if (initialCache && apolloClientRef.current) {
      const currentCache = apolloClientRef.current.cache.extract()
      const isCacheMatch = isMatch(currentCache, initialCache)

      if (!isCacheMatch) {
        merge(currentCache, initialCache)
        apolloClientRef.current.cache.restore(currentCache)
      }
    }
  }

  if (pathName !== currentPathName.current || businessRef.current !== businessId) {
    if (businessRef.current !== businessId) {
      apolloClientRef.current = initializeApollo({ businessId })
    }
    initializeCache()
    businessRef.current = businessId
    currentPathName.current = pathName
  }

  return apolloClientRef.current
}
